import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import type { DeckShareStatus } from '@/utils/api-pb';

interface DeckShareDialogProps {
  status: DeckShareStatus | null;
  isLoading: boolean;
  isBusy: boolean;
  onEnable: () => void;
  onRevoke: () => void;
}

/**
 * The share panel: three states, and the warning that belongs with the middle one.
 *
 * Off — one button and a plain sentence about what a link means. On — the address to
 * copy plus a revoke. Loading — a loader, because the status comes from the server and
 * guessing it here is how a UI ends up telling someone their deck is private while a
 * live link is circulating.
 *
 * The warning is deliberately not softened: anyone with the link can open the deck,
 * including whoever it was forwarded to. Revoking is the answer to that, and it is one
 * click away rather than buried behind a confirmation.
 */
export function DeckShareDialog({ status, isLoading, isBusy, onEnable, onRevoke }: DeckShareDialogProps) {
  const { t } = useTranslation('builder');
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!status?.url) return;
    try {
      await navigator.clipboard.writeText(status.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена может быть закрыт политикой браузера — адрес и так на экране,
      // его можно выделить руками.
    }
  }

  if (isLoading) {
    return <AccessibleLoader label={t('loading', { ns: 'common' })} />;
  }

  if (!status?.enabled || !status.url) {
    return (
      <div className="flex flex-col gap-[16px]">
        <p className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
          {t('deck.share.off.text')}
        </p>
        <Button variant="brand" size="brand" onClick={onEnable} disabled={isBusy}>
          {t('deck.share.off.action')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="flex flex-col gap-[8px]">
        <label
          htmlFor="deck-share-url"
          className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]"
        >
          {t('deck.share.on.label')}
        </label>
        <div className="flex flex-wrap gap-[8px]">
          <Input
            id="deck-share-url"
            value={status.url}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
            className="h-12 flex-1 rounded-xl border-[#eeeeee] text-[14px]"
          />
          <Button variant="brand" size="brand" onClick={copy}>
            {copied ? t('deck.share.on.copied') : t('deck.share.on.copy')}
          </Button>
        </div>
      </div>

      <p className="rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[16px] py-[12px] font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
        {t('deck.share.on.warning')}
      </p>

      <Button variant="brand-danger" size="brand" onClick={onRevoke} disabled={isBusy}>
        {t('deck.share.on.revoke')}
      </Button>
    </div>
  );
}
